import { useLocation } from '@reach/router';
import { graphql, useStaticQuery } from 'gatsby';
import { useMemo } from 'react';

import { useCurrentLang } from '~/hooks/useCurrentLang';

import { LanguageSwitchHookQuery, UseLanguageSwitchHook } from './models';
import { isPageNotFound, resolveNotFoundPageUrl } from './utils';

export const useLanguageSwitch: UseLanguageSwitchHook = (brand) => {
  const {
    languages,
    currentLang: { locale: currentLangCode },
  } = useCurrentLang();

  const { pathname } = useLocation();

  const {
    allSitePage: { nodes: pageNodes },
    allContentstackSettings: { nodes: settingsNodes },
  } = useStaticQuery<LanguageSwitchHookQuery>(graphql`
    {
      allSitePage {
        nodes {
          id
          path
          context {
            uid
            locale
          }
        }
      }
      allContentstackSettings {
        nodes {
          languageSwitch: language_switch {
            languageName: language_name
            missingTranslation: missing_translation
            navButtonAria: nav_button_aria
          }
          tags
          publishDetails: publish_details {
            locale
          }
        }
      }
    }
  `);

  const currentPage = useMemo(
    () => pageNodes.find(({ path }) => path === pathname) || null,
    [pageNodes, pathname]
  );

  const list = useMemo(() => {
    const filteredPageNodes = pageNodes.filter(
      (node) => node.context.uid === currentPage?.context.uid
    );

    const filteredSettingsNodes = settingsNodes.filter(({ tags }) =>
      tags.includes(brand)
    );

    return languages.map((language) => {
      const { locale, url: langUrl } = language;
      const foundPageNode = filteredPageNodes.find(
        (node) => node.context.locale === locale
      );

      const foundLangSettingsNode = filteredSettingsNodes.find(
        (node) => node.publishDetails.locale === locale
      )!;

      if (foundPageNode) {
        return {
          currentPage: currentLangCode === foundPageNode.context.locale,
          locale: foundPageNode.context.locale,
          url: foundPageNode.path,
          translated: true,
          languageSwitchSettings: foundLangSettingsNode.languageSwitch,
        };
      }

      let url = langUrl;
      let translated = false;

      if (isPageNotFound(currentPage, pathname)) {
        url = resolveNotFoundPageUrl(
          langUrl,
          currentLangCode,
          languages,
          pathname
        );
        translated = true;
      }

      return {
        currentPage: currentLangCode === locale,
        locale,
        url,
        translated,
        languageSwitchSettings: foundLangSettingsNode.languageSwitch,
      };
    });
  }, [
    currentLangCode,
    currentPage,
    languages,
    pageNodes,
    settingsNodes,
    brand,
  ]);

  return { list, currentPage };
};
